import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { ApiError, api, onUnauthorized, resetCsrf } from '../api/client'
import type { Me } from '../api/types'
import { clearApiCache } from '../pwa'
import { clearOfflineSession, loadOfflineSession, saveOfflineSession } from './offlineSession'
import { AuthContext, type AuthState } from './AuthContext'

const ME_KEY = ['me'] as const

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient()

  const me = useQuery<Me | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        const me = await api.get<Me>('/api/auth/me/', { silent401: true })
        void saveOfflineSession(me)
        return me
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          await clearOfflineSession()
          return null
        }
        if (error instanceof ApiError && error.status === 0) return loadOfflineSession() // offline: last known profile
        throw error
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  })

  useEffect(
    () =>
      onUnauthorized(() => {
        client.setQueryData(ME_KEY, null)
        void clearOfflineSession()
      }),
    [client],
  )

  const login = useCallback(
    async (username: string, password: string) => {
      const user = await api.post<Me>('/api/auth/login/', { username, password })
      resetCsrf() // Django rotates the CSRF token on login
      client.setQueryData(ME_KEY, user)
      void saveOfflineSession(user)
      return user
    },
    [client],
  )

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout/')
    } finally {
      resetCsrf()
      void clearApiCache()
      void clearOfflineSession()
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] })
      client.setQueryData(ME_KEY, null)
    }
  }, [client])

  const value = useMemo<AuthState>(() => {
    const user = me.data ?? null
    const status = me.isPending ? 'loading' : user ? 'authenticated' : 'anonymous'
    return { status, user, login, logout }
  }, [me.data, me.isPending, login, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
